/**
 * Simple oscillator-based synthesizer
 * Uses Web Audio API's OscillatorNode
 */

import { Synthesizer, SynthesizerConfig, generateNoteId } from './Synthesizer';

/**
 * Oscillator waveform type
 */
export type OscillatorWaveform = 'sine' | 'square' | 'sawtooth' | 'triangle';

/**
 * Oscillator synth configuration
 */
export interface OscillatorSynthConfig extends SynthesizerConfig {
  waveform?: OscillatorWaveform;
  detune?: number; // in cents
}

/**
 * Oscillator synthesizer
 */
export class OscillatorSynth extends Synthesizer {
  private waveform: OscillatorWaveform = 'sawtooth';
  private detune: number = 0;

  constructor(config: OscillatorSynthConfig = {}) {
    super(config);
    if (config.waveform) {
      this.waveform = config.waveform;
    }
    if (config.detune !== undefined) {
      this.detune = config.detune;
    }
  }

  /**
   * Note on (start playing a note)
   */
  noteOn(pitch: number, velocity: number, noteId?: string): string {
    if (!this.context || !this.masterGain) {
      console.warn('Synthesizer not initialized');
      return '';
    }

    const id = noteId || generateNoteId();

    // Stop existing note with same ID first
    if (this.activeNotes.has(id)) {
      this.noteOff(id);
    }

    const now = this.context.currentTime;

    // Create oscillator
    const oscillator = this.context.createOscillator();
    oscillator.type = this.waveform;
    oscillator.frequency.value = this.midiNoteToFrequency(pitch);
    oscillator.detune.value = this.detune;

    // Create individual gain node (for envelope)
    const gainNode = this.context.createGain();
    gainNode.gain.value = 0;

    // Connect: oscillator -> gainNode -> masterGain -> destination
    oscillator.connect(gainNode);
    gainNode.connect(this.masterGain);

    // Apply ADSR envelope
    this.applyADSR(gainNode, velocity, now);

    // Start playback
    oscillator.start(now);

    // Record as active note
    this.activeNotes.set(id, {
      noteId: id,
      oscillator,
      gainNode,
      startTime: now,
    });

    return id;
  }

  /**
   * Note off (stop playing a note)
   */
  noteOff(noteId: string): void {
    const note = this.activeNotes.get(noteId);
    if (!note || !this.context) {
      return;
    }

    const now = this.context.currentTime;

    // Apply release envelope
    this.applyRelease(note.gainNode, now);

    // Stop after release
    if (note.oscillator) {
      try {
        note.oscillator.stop(now + this.envelope.release + 0.01);
      } catch (e) {
        // Ignore if already stopped
      }
    }

    // Cleanup after a short delay
    // Note: Use noteRef for identity check. This prevents accidentally
    // deleting a new note created with the same noteId.
    const noteRef = note;
    setTimeout(() => {
      if (noteRef.oscillator) {
        try { noteRef.oscillator.disconnect(); } catch (e) {}
      }
      try { noteRef.gainNode.disconnect(); } catch (e) {}
      // Only remove from Map if no new note was created with the same noteId
      if (this.activeNotes.get(noteId) === noteRef) {
        this.activeNotes.delete(noteId);
      }
    }, (this.envelope.release + 0.1) * 1000);
  }

  /**
   * Stop all notes immediately
   */
  allNotesOff(): void {
    if (!this.context) return;

    // Stop all notes immediately
    for (const [noteId, note] of this.activeNotes) {
      // 1. Disconnect gain first (immediately cut output)
      try {
        note.gainNode.disconnect();
      } catch (e) {}

      // 2. Set gain to 0
      try {
        note.gainNode.gain.cancelScheduledValues(0);
        note.gainNode.gain.setValueAtTime(0, this.context.currentTime);
      } catch (e) {}

      // 3. Stop and disconnect oscillator
      if (note.oscillator) {
        try {
          note.oscillator.stop();
        } catch (e) {}
        try {
          note.oscillator.disconnect();
        } catch (e) {}
      }
    }

    // Clear map
    this.activeNotes.clear();
  }

  /**
   * Set waveform
   */
  setWaveform(waveform: OscillatorWaveform): void {
    this.waveform = waveform;
    // Does not affect currently playing notes
  }

  /**
   * Get waveform
   */
  getWaveform(): OscillatorWaveform {
    return this.waveform;
  }

  /**
   * Set detune (in cents)
   */
  setDetune(detune: number): void {
    this.detune = detune;
  }

  /**
   * Get detune
   */
  getDetune(): number {
    return this.detune;
  }
}

/**
 * Create default oscillator synth
 */
export function createDefaultOscillatorSynth(): OscillatorSynth {
  return new OscillatorSynth({
    waveform: 'sawtooth',
    volume: 0.5,
    envelope: {
      attack: 0.01,
      decay: 0.1,
      sustain: 0.7,
      release: 0.2,
    },
  });
}

/**
 * Create piano-like oscillator synth
 */
export function createPianoLikeSynth(): OscillatorSynth {
  return new OscillatorSynth({
    waveform: 'triangle',
    volume: 0.6,
    envelope: {
      attack: 0.005,
      decay: 0.3,
      sustain: 0.4,
      release: 0.5,
    },
  });
}

/**
 * Create pad-style oscillator synth
 */
export function createPadSynth(): OscillatorSynth {
  return new OscillatorSynth({
    waveform: 'sine',
    volume: 0.5,
    envelope: {
      attack: 0.3,
      decay: 0.2,
      sustain: 0.8,
      release: 1.0,
    },
  });
}

/**
 * Create bass-style oscillator synth
 */
export function createBassSynth(): OscillatorSynth {
  return new OscillatorSynth({
    waveform: 'square',
    volume: 0.4,
    envelope: {
      attack: 0.01,
      decay: 0.2,
      sustain: 0.6,
      release: 0.15,
    },
  });
}
